import { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, MousePointer, TrendingUp, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/shop`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-slate-500">
        Errore nel caricamento delle statistiche
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm stats-card" data-testid="total-views-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{data.totale_visualizzazioni}</p>
              <p className="text-xs text-slate-500">Visualizzazioni totali</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm stats-card" data-testid="total-clicks-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <MousePointer className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{data.totale_click}</p>
              <p className="text-xs text-slate-500">Click "Vai al negozio"</p>
            </div>
          </div>
        </div>
      </div>

      {/* Products analytics */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <h3 className="font-outfit font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Statistiche per prodotto
        </h3>

        {data.prodotti.length > 0 ? (
          <div className="space-y-3">
            {data.prodotti.map((product) => (
              <div
                key={product.id}
                className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl"
                data-testid={`product-analytics-${product.id}`}
              >
                {/* Image */}
                {product.immagine && (
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                    <img
                      src={product.immagine}
                      alt={product.nome}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-700 truncate">{product.nome}</p>
                </div>

                {/* Stats */}
                <div className="flex gap-4 flex-shrink-0">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-slate-800">{product.visualizzazioni}</p>
                    <p className="text-xs text-slate-500">Viste</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-green-600">{product.click_negozio}</p>
                    <p className="text-xs text-slate-500">Click</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm text-center py-6">
            Nessun dato disponibile. Aggiungi prodotti per vedere le statistiche.
          </p>
        )}
      </div>
    </div>
  );
}
